import React, { Suspense, useEffect, useState, useCallback } from 'react';
import { Route, Switch, useRouteMatch, useLocation } from 'react-router-dom';
import { actions } from '@airasia-hotels/core';
import styled from 'styled-components';
import LocaleProvider from '@airasia-common/libraries/i18n/LocaleProvider';
import { bool, func, object } from 'prop-types';
import { connect } from 'react-redux';
import Cookies from 'js-cookie';
import queryString from 'query-string';
import { UHF } from '@airasia-common/libraries/constants/UHF';
import LoadingWrapper from '@airasia-common/react-dom/components/Wrapper/LoadingWrapper';
import Column from '@airasia-common/react-dom/components/Container/Column';
import Wrapper from '@airasia-common/react-dom/components/Wrapper/Wrapper';
import { Header } from '@airasia-common/react-dom/components/HeaderV2';
import { isLoggedUser } from '@airasia-common/libraries/utilities/CredentialHelper';
import {
  HOTEL_LISTING_PAGE,
  HOTEL_DETAIL_PAGE,
  GUEST_DETAIL_PAGE,
  BOOKING_CONFIRMATION_PAGE,
} from '@airasia-hotels/core/redux/analytics/analytics.constants';
import loadable from 'loadable-components';
import HotelFooter from '../../components/HotelFooter';
import { useCredential, getSSOInfo } from '../../hooks/useCredential';
import {
  DISCOUNT_MODAL_SHOW_FOR,
  DISCOUNT_MODAL_SHOW_ON,
  ROUTE,
  SESSION_STORAGE_DISCOUNT_MODAL_OPENED,
  UHF_LOCALE_NAME,
  COUNTRY_CODE_DEFAULT,
  LANGUAGE_DEFAULT,
  WITHOUT_FOOTER,
  COOKIE_GA_ID,
} from '../../utils/constants';
import {
  useFlagHotelMandateLogin,
  useFlagRebrandEnabled,
  useEnableSupportedCurrencies,
} from '../../hooks/useFeatureFlag';
import { hotel_mandatory_login } from '../Experiments';
import useSessionStorage from '../../hooks/useSessionStorage';
import LoadingComponents from '../../components/Loading';

const DiscountModal = loadable(() =>
  import(
    /* webpackChunkName:"DiscountModal" */ '../../components/DiscountModal'
  )
);
const HotelDetail = loadable(() =>
  import(/* webpackChunkName:"HotelDetail" */ './HotelDetail')
);
const HotelList = loadable(() =>
  import(/* webpackChunkName:"HotelList" */ './HotelList')
);

const BookingDetail = loadable(() =>
  import(/* webpackChunkName:"BookingDetail" */ './BookingDetail')
);

const TripDetail = loadable(() =>
  import(/* webpackChunkName:"TripDetail" */ './TripDetail')
);

const BookingConfirmation = loadable(() =>
  import(/* webpackChunkName:"BookingConfirmation" */ './BookingConfirmation')
);
const ErrorPage = loadable(() =>
  import(/* webpackChunkName:"ErrorPage" */ './ErrorPage')
);
const Maps = loadable(() => import(/* webpackChunkName:"Maps" */ './Maps'));

const UHFHeaderWrapper = styled.div`
  ${({ isMobileView }) => {
    return isMobileView ? '' : 'min-height: 64px';
  }};
`;

const UHFHeader = styled.div`
  position: relative;
  z-index: 999;
  ${({ isMobileView, mandateLogin }) => {
    return isMobileView
      ? mandateLogin
        ? 'z-index: 10; height: 0px;'
        : 'display: none'
      : '';
  }};
`;

const UHF_LIST_MENU = ['aalogo', 'localecurrency', 'login'];

const getUHFConfigFromData = (data, language, currency, agentId) => {
  if (!data) return false;

  // GET component mapping
  const components = data.components;
  const componentsData = components[0].data;
  const componentsUHFData = [];
  const agentHeaderData =
    (agentId && {
      type: 'custom-component',
      id: 'AgentHeader',
      iconUrl: '',
      label: '',
      redirectUrl: '',
      direction: 'right',
      newTab: false,
      data: null,
    }) ||
    null;

  componentsData.forEach(e => {
    if (UHF_LIST_MENU.indexOf(e.id) !== -1) {
      componentsUHFData.push(e);
    }
  });

  if (agentHeaderData) {
    const login = componentsUHFData.find(e => e.id === 'login');
    if (login) {
      const index = componentsUHFData.indexOf(login);
      componentsUHFData[index] = agentHeaderData;
    } else {
      componentsUHFData.push(agentHeaderData);
    }
  }
  data.components = [{ data: componentsUHFData }];

  // GET language mapping
  const locales = data.localeList;
  const localesData = locales[0].data;
  const localesUHFData = [];
  localesData.forEach(e => {
    if (language.indexOf(e.default) !== -1) {
      localesUHFData.push(e);
    }
  });
  data.localeList = [{ data: localesUHFData }];

  // GET currency mapping
  const currencies = data.currencyList;
  const currenciesData = currencies[0].data;
  const currenciesUHFData = [];
  currenciesData.forEach(e => {
    if (currency.indexOf(e.shortform) !== -1) {
      currenciesUHFData.push(e);
    }
  });
  data.currencyList = [{ data: currenciesUHFData }];
  return data;
};

const Hotel = ({
  filter,
  featureFlags,
  hotelConfig,
  fetchCountries,
  fetchHotelFeatureFlags,
  fetchHotelConfig,
  updateCurrency,
  setUserProfile,
  isFetchingFeatureFlags,
  isFetchingHotelConfig,
  fetchHeaderCurrencies,
  fetchUHFData,
  uhfData,
  isMobileView,
  fetchUHSWData,
  fetchPopupData,
  popupData,
  fetchTranslations,
  translations,
}) => {
  const credential = useCredential();

  const isTripDetail = useRouteMatch(ROUTE.HOTEL.TRIP_DETAIL, {
    exact: true,
  })?.isExact;

  const isHotelListWithoutIndex = useRouteMatch(ROUTE.HOTEL.HOTEL_LIST, {
    exact: true,
  })?.isExact;

  const isHotelListWithIndex = useRouteMatch(ROUTE.HOTEL.HOTEL_LIST_INDEX, {
    exact: true,
  })?.isExact;

  const isHotelList = isHotelListWithoutIndex || isHotelListWithIndex;

  let isHotelDetail = false;
  const hotelDetailRoute = useRouteMatch(ROUTE.HOTEL.HOTEL_DETAIL, {
    exact: true,
  });
  if (hotelDetailRoute && hotelDetailRoute.isExact) {
    const { params = {} } = hotelDetailRoute;
    if (params.hotelId !== 'search') {
      isHotelDetail = true;
    }
  }
  const [isValidateUserLogin, setIsValidateUserLogin] = useState();

  const [memorizedFilter] = useSessionStorage('hotelFilter', filter);
  const [showLocaleAndCurrency, setShowLocaleAndCurrency] = useState(true);

  const [isOpenDiscountModal, setIsOpenDiscountModal] = useState(false);
  const [discountModalData, setDiscountModalData] = useState({});
  const isLoggedIn = isLoggedUser(credential);
  const [
    isDiscountModalLoggedInContent,
    setIsDiscountModalLoggedInContent,
  ] = useState(false);
  const geoCountry = sessionStorage.getItem('geocountry');
  const [mandateLogin, setMandateLogin] = useState(false);
  const isUseMandateLogin = useFlagHotelMandateLogin();
  const move = useFlagRebrandEnabled();

  const location = useLocation();
  const { pathname } = location;
  const lemonAgentToken = Cookies.get('lemonAgent');

  useEffect(() => {
    // the logic is hide when in trip detail page
    if (isTripDetail) {
      setShowLocaleAndCurrency(false);
    } else {
      setShowLocaleAndCurrency(true);
    }
  }, [isTripDetail]);

  // If we're at Guest Detail page and filter hasn't been initialized,
  // it means user was coming back from check-out page,
  // use memorized filter instead.
  const restoredFilter =
    isTripDetail && !filter.isInit ? memorizedFilter : filter;

  useEffect(() => {
    if (restoredFilter.isInit) {
      fetchCountries({
        languageInput: restoredFilter.languageId,
        pageId: isHotelList
          ? HOTEL_LISTING_PAGE
          : isHotelDetail
          ? HOTEL_DETAIL_PAGE
          : isTripDetail
          ? GUEST_DETAIL_PAGE
          : BOOKING_CONFIRMATION_PAGE,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restoredFilter]);

  useEffect(() => {
    fetchHotelFeatureFlags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const supportedCurrencies = useEnableSupportedCurrencies();

  useEffect(() => {
    if (!isFetchingFeatureFlags) {
      fetchHotelConfig(supportedCurrencies);
    }
  }, [isFetchingFeatureFlags]);

  useEffect(() => {
    if (filter.isInit) {
      fetchHeaderCurrencies({ language: filter.language });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.isInit, filter.language]);

  useEffect(() => {
    if (filter.isInit) {
      fetchUHFData({ language: filter.languageId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.isInit, filter.languageId, restoredFilter.languageId]);

  useEffect(() => {
    if (filter.languageId) {
      fetchTranslations({ language: filter.languageId });
    }
  }, [filter.languageId]);

  useEffect(() => {
    if (filter.isInit) {
      fetchUHSWData({ language: filter.languageId });
      fetchPopupData({
        language: filter.languageId,
        country: geoCountry ? geoCountry.toLowerCase() : 'my',
      });
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.isInit, filter.languageId]);

  const channel = queryString.parse(window.location.search)?.channel;

  useEffect(() => {
    const { countryCode, languageId } = filter;
    if (
      (!lemonAgentToken || lemonAgentToken === '') &&
      isTripDetail &&
      isUseMandateLogin
    ) {
      const gaId = Cookies.get(COOKIE_GA_ID) || process.env.REACT_APP_GTM_ID;
      hotel_mandatory_login(
        {
          platform: isMobileView ? 'mweb' : 'dweb',
          countryCode: countryCode || COUNTRY_CODE_DEFAULT,
          locale: languageId || LANGUAGE_DEFAULT,
          user_id: gaId,
          domain: channel,
        },
        flag => setMandateLogin(flag)
      );
    }
  }, [
    isUseMandateLogin,
    isMobileView,
    isTripDetail,
    filter.countryCode,
    filter.languageId,
    filter,
    channel,
    lemonAgentToken,
  ]);

  let language = UHF.DEFAULT_LANGUAGE;

  if (featureFlags.ENABLE_HOTEL_WEB_LANGUAGE_SELECTION) {
    language =
      queryString.parse(window.location.search).languageId ||
      Cookies.get(UHF_LOCALE_NAME) ||
      language;

    Cookies.set(UHF_LOCALE_NAME, language);
  }

  const supportedLanguages =
    (hotelConfig && hotelConfig.find(item => !!item.locale)?.locale) || [];

  const supportedCurrency =
    (hotelConfig && hotelConfig.find(item => !!item.currency)?.currency) || [];

  const { headerData, footerData } = uhfData || {};
  const { lemonAgentId } = filter || null;
  // if user is coming from leman duty flow in that case we will keep his/har soo login flow otherwise it will we leman agent flow.
  const agentId =
    lemonAgentId === process.env.REACT_APP_LEMON_DUTYTRAVEL_AGENT_ID
      ? ''
      : lemonAgentId;
  const uhfConfig = getUHFConfigFromData(
    headerData,
    supportedLanguages,
    supportedCurrency,
    agentId,
    filter.languageId,
    filter.currency
  );

  const isCheckDiscountModalShowOnPage = showOnPage => {
    if (
      isHotelList &&
      (showOnPage === DISCOUNT_MODAL_SHOW_ON.LISTING ||
        showOnPage === DISCOUNT_MODAL_SHOW_ON.LISTING_DETAILS)
    )
      return true;

    if (
      isHotelDetail &&
      (showOnPage === DISCOUNT_MODAL_SHOW_ON.DETAILS ||
        showOnPage === DISCOUNT_MODAL_SHOW_ON.LISTING_DETAILS ||
        showOnPage === DISCOUNT_MODAL_SHOW_ON.DETAILS_GUEST_DETAILS)
    )
      return true;

    if (
      isTripDetail &&
      (showOnPage === DISCOUNT_MODAL_SHOW_ON.GUEST_DETAILS ||
        showOnPage === DISCOUNT_MODAL_SHOW_ON.DETAILS_GUEST_DETAILS)
    )
      return true;

    if (showOnPage === DISCOUNT_MODAL_SHOW_ON.ALL) return true;

    return false;
  };

  const isCheckDiscountModalShowForUser = useCallback(
    showForUser => {
      if (showForUser === DISCOUNT_MODAL_SHOW_FOR.ALL) {
        const SSOInfo = getSSOInfo();
        if (SSOInfo) {
          if (isLoggedIn) {
            setIsDiscountModalLoggedInContent(true);
            return true;
          }
        } else {
          return true;
        }
      }

      if (showForUser === DISCOUNT_MODAL_SHOW_FOR.NON_LOGGED_IN) return true;

      if (showForUser === DISCOUNT_MODAL_SHOW_FOR.LOGGED_IN && isLoggedIn) {
        setIsDiscountModalLoggedInContent(true);
        return true;
      }

      return false;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [isLoggedIn]
  );

  useEffect(() => {
    if (isHotelList && popupData[DISCOUNT_MODAL_SHOW_ON.LISTING]) {
      setDiscountModalData(popupData[DISCOUNT_MODAL_SHOW_ON.LISTING]);
    }
    if (isHotelDetail && popupData[DISCOUNT_MODAL_SHOW_ON.DETAILS]) {
      setDiscountModalData(popupData[DISCOUNT_MODAL_SHOW_ON.DETAILS]);
    }
    if (isTripDetail && popupData[DISCOUNT_MODAL_SHOW_ON.GUEST_DETAILS]) {
      setDiscountModalData(popupData[DISCOUNT_MODAL_SHOW_ON.GUEST_DETAILS]);
    }
  }, [popupData, isHotelList, isHotelDetail, isTripDetail]);

  useEffect(() => {
    const { config = {} } = popupData;
    if (
      !JSON.parse(
        sessionStorage.getItem(SESSION_STORAGE_DISCOUNT_MODAL_OPENED)
      ) &&
      isCheckDiscountModalShowOnPage(config.showOnPage) &&
      isCheckDiscountModalShowForUser(config.showForUser, isLoggedIn)
    ) {
      setIsOpenDiscountModal(true);
      sessionStorage.setItem(
        SESSION_STORAGE_DISCOUNT_MODAL_OPENED,
        JSON.stringify(true)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [popupData, isLoggedIn]);

  const onCloseDiscountModal = () => {
    if (document.getElementsByTagName('body')) {
      document.getElementsByTagName('body')[0].removeAttribute('style');
    }
    setIsOpenDiscountModal(false);
  };

  return (
    <>
      <Column display="block" flex={1}>
        <LocaleProvider
          locale={restoredFilter.languageId}
          isInit={restoredFilter.isInit}
          localePhrases={translations}
        >
          {pathname !== ROUTE.HOTEL.MAP_VIEW && (
            <UHFHeaderWrapper isMobileView={isMobileView}>
              {restoredFilter.isInit && uhfConfig && (
                <UHFHeader
                  isMobileView={isMobileView}
                  mandateLogin={mandateLogin}
                >
                  <Header
                    agentId={agentId}
                    credential={credential}
                    setUserProfile={setUserProfile}
                    updateCurrency={updateCurrency}
                    languageParamName="languageId"
                    uhfConfig={{
                      viewport: isMobileView ? 'mobile' : 'desktop',
                      data: uhfConfig,
                      currency: filter.currency,
                      locale: filter.languageId,
                      environment:
                        process.env.REACT_APP_ENVIRONMENT !== 'PRODUCTION'
                          ? 'staging'
                          : 'production',
                      showCurrency: showLocaleAndCurrency,
                      showLocale: showLocaleAndCurrency,
                      loginConfig: {
                        reloadOnLogin: isTripDetail ? !mandateLogin : true,
                        isLoginMandated: isTripDetail ? mandateLogin : false,
                      },
                      onUserLoginStatus: e => {
                        setIsValidateUserLogin(e);
                      },
                    }}
                    theme={move && 'AIRASIA_MOVE'}
                    move={move}
                  />
                </UHFHeader>
              )}
            </UHFHeaderWrapper>
          )}
          <LoadingWrapper
            fetching={isFetchingFeatureFlags || isFetchingHotelConfig}
          >
            <Suspense fallback={<LoadingComponents />}>
              <Switch>
                <Route exact path={ROUTE.HOTEL.ERROR}>
                  <ErrorPage />
                </Route>
                <Route exact path={ROUTE.HOTEL.HOTEL_LIST}>
                  <HotelList />
                </Route>
                <Route exact path={ROUTE.HOTEL.HOTEL_LIST_INDEX}>
                  <HotelList />
                </Route>
                <Route exact path={ROUTE.HOTEL.BOOKING_DETAIL}>
                  <BookingDetail />
                </Route>
                <Route exact path={ROUTE.HOTEL.CONFIRMATION}>
                  <BookingConfirmation />
                </Route>
                <Route exact path={ROUTE.HOTEL.TRIP_DETAIL}>
                  <TripDetail
                    isValidateUserLogin={
                      lemonAgentToken && lemonAgentToken !== ''
                        ? false
                        : isValidateUserLogin
                    }
                    mandateLogin={mandateLogin}
                  />
                </Route>
                <Route exact path={ROUTE.HOTEL.MAP_VIEW}>
                  <Maps />
                </Route>
                <Route exact path={ROUTE.HOTEL.HOTEL_DETAIL_OLD}>
                  <HotelDetail />
                </Route>
                <Route exact path={ROUTE.HOTEL.HOTEL_DETAIL}>
                  <HotelDetail />
                </Route>
              </Switch>
            </Suspense>
          </LoadingWrapper>
        </LocaleProvider>
      </Column>
      {!isMobileView && WITHOUT_FOOTER.indexOf(pathname) === -1 && (
        <Wrapper pt={24}>
          <HotelFooter data={footerData} />
        </Wrapper>
      )}
      {isOpenDiscountModal && discountModalData.headerText && (
        <DiscountModal
          isOpen={isOpenDiscountModal}
          isMobileView={isMobileView}
          data={discountModalData}
          onCloseDiscountModal={onCloseDiscountModal}
          isLoggedInContent={isDiscountModalLoggedInContent}
        />
      )}
    </>
  );
};

Hotel.propTypes = {
  filter: object,
  featureFlags: object,
  hotelConfig: object,
  fetchCountries: func,
  fetchHotelFeatureFlags: func,
  updateCurrency: func,
  setUserProfile: func,
  fetchHotelConfig: func,
  isFetchingHotelConfig: bool,
  isFetchingFeatureFlags: bool,
  fetchHeaderCurrencies: func,
  fetchUHFData: func,
  uhfData: object,
  isMobileView: bool,
  fetchUHSWData: func,
  fetchPopupData: func,
  popupData: object,
};

const mapStateToProps = ({ filter, metadata }) => ({
  filter: filter.hotel,
  featureFlags: metadata.featureFlags,
  hotelConfig: metadata.hotelConfig,
  isFetchingHotelConfig: metadata.isFetchingHotelConfig,
  isFetchingFeatureFlags: metadata.isFetchingFeatureFlags,
  uhfData: metadata.uhfData || {},
  popupData: metadata.popupData || {},
  translations: metadata.hotelTranslations,
});

const mapDispatchToProps = {
  fetchCountries: actions.metadataActions.fetchCountries,
  fetchHotelConfig: actions.metadataActions.fetchHotelConfig,
  fetchHotelFeatureFlags: actions.metadataActions.fetchHotelFeatureFlags,
  updateCurrency: actions.filterActions.updateHotelCurrency,
  setUserProfile: actions.userActions.setUserProfile,
  fetchHeaderCurrencies: actions.metadataActions.fetchHeaderCurrencies,
  fetchUHFData: actions.metadataActions.fetchUHFData,
  fetchUHSWData: actions.metadataActions.fetchUHSWData,
  fetchPopupData: actions.metadataActions.fetchPopupData,
  fetchTranslations: actions.metadataActions.fetchHotelTranslations,
};

export default connect(mapStateToProps, mapDispatchToProps)(Hotel);
